import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { BaseModel, TariffConfiguration, Scenario, KappaConfig } from '../types/model';
import { apiService } from '../services/api';
import { MODEL_TARGETS, ModelTarget } from '../utils/modelTargets';

// Storage key for localStorage
const STORAGE_KEY = 'trade_model_scenarios';
// Bump when backend model results change (invalidates stale localStorage)
const CACHE_VERSION = '5';

interface StoredScenarioData {
  version?: string;
  framework: string;
  dataset: string;
  scenarios: Scenario[];
  selectedScenarioIds: string[];
}

interface ModelState {
  framework: string;
  dataset: string;
  baselineModel: BaseModel | null;
  baselines: Record<string, BaseModel>;
  scenarios: Scenario[];
  selectedScenarioIds: string[];
  isLoading: boolean;
  error: string | null;
  solvingCount: number;
  storageWarning: boolean;
  cachedScenariosList: CachedScenarioMeta[];
  frameworkInfo: { [framework: string]: { supports_kappa?: boolean } };
}

// Queue item for scenario solving. Shocks compose: a scenario carries a
// tariff config (always) and an optional kappa config — both present solve
// together in one job.
interface SolveQueueItem {
  framework: string;
  dataset: string;
  id: string;
  name: string;
  tariffConfig: TariffConfiguration;
  kappaConfig?: KappaConfig;
  isUpdate: boolean;
}

interface ModelContextType extends ModelState {
  ensureBaseline: (framework: string, dataset: string, refresh?: boolean) => Promise<BaseModel>;
  importChatScenario: (framework: string, dataset: string, scenario: Scenario) => void;
  setFramework: (framework: string) => void;
  setDataset: (dataset: string) => void;
  loadModel: (framework: string, dataset: string) => Promise<void>;
  clearError: () => void;
  // Scenario methods. tariffConfig is always required (may be EMPTY_TARIFF_CONFIG);
  // kappaConfig is optional (Furusawa2023-only) and composes with the tariff shock.
  addScenario: (target: ModelTarget, name: string, tariffConfig: TariffConfiguration, kappaConfig?: KappaConfig) => Promise<Scenario>;
  updateScenario: (id: string, name: string, tariffConfig: TariffConfiguration, kappaConfig?: KappaConfig) => Promise<Scenario>;
  saveScenarioMetadata: (id: string, name: string, tariffConfig: TariffConfiguration, kappaConfig?: KappaConfig) => void;
  deleteScenario: (id: string) => void;
  updateScenarioSelection: (ids: string[]) => void;
  getSelectedScenarios: () => Scenario[];
  clearAllScenarios: () => void;
  getNextScenarioName: () => string;
  // Caching methods
  saveScenarioToCache: (scenarioId: string) => Promise<void>;
  deleteScenarioFromCache: (scenarioId: string) => Promise<void>;
  loadMultipleCachedScenarios: (scenarioIds: string[]) => Promise<void>;
  clearAllCache: () => Promise<void>;
  refreshCachedScenariosList: () => Promise<void>;
}

// Metadata for cached scenarios (not full data)
export interface CachedScenarioMeta {
  framework: string;
  dataset: string;
  id: string;
  name: string;
  created_at: string;
  tariff_summary?: {
    importers: number;
    exporters: number;
    sectors: number;
    changes: number;
  };
}

const ModelContext = createContext<ModelContextType | undefined>(undefined);

// Note: scenarioCounter, solveQueue, isProcessingQueue are managed
// via useRef inside ModelProvider to avoid shared mutable state across
// multiple React mount/unmount cycles.

// Helper to save scenarios to localStorage.
// Fix 7: the setItem call is wrapped in its own try/catch so quota errors
// are reported with the payload size; the outer catch handles JSON errors.
const saveToStorage = (framework: string, dataset: string, scenarios: Scenario[], selectedIds: string[]) => {
  try {
    // Only save solved scenarios (can't persist solving state)
    const persistableScenarios = scenarios.filter(s => s.status === 'solved' || s.status === 'error');
    const data: StoredScenarioData = {
      version: CACHE_VERSION,
      framework,
      dataset,
      scenarios: persistableScenarios,
      selectedScenarioIds: selectedIds.filter(id => persistableScenarios.some(s => s.id === id)),
    };
    const serialized = JSON.stringify(data);
    try {
      localStorage.setItem(STORAGE_KEY, serialized);
      return true;
    } catch (e) {
      console.warn(`Failed to save scenarios to localStorage (payload: ${serialized.length} bytes):`, e);
      return false;
    }
  } catch (e) {
    console.warn('Failed to serialize scenarios for localStorage:', e);
    return false;
  }
};

const loadFromStorage = (): { scenarios: Scenario[]; selectedIds: string[] } | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data: StoredScenarioData = JSON.parse(stored);
    // Restore only the current result schema.
    if (data.version === CACHE_VERSION) {
      // Restore Date objects.
      const scenarios = data.scenarios
        .map(s => ({
          ...s,
          createdAt: new Date(s.createdAt),
        }));
      return { scenarios, selectedIds: data.selectedScenarioIds };
    }
    return null;
  } catch (e) {
    console.warn('Failed to load scenarios from localStorage:', e);
    return null;
  }
};

export const ModelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const scenarioCounterRef = useRef(0);
  const solveQueueRef = useRef<SolveQueueItem[]>([]);
  const isProcessingQueueRef = useRef(false);
  // Refs keep current framework/dataset/scenarios accessible from stable callbacks
  // without stale closures. Updated synchronously in a useEffect after each render.
  const frameworkRef = useRef('');
  const datasetRef = useRef('');
  const scenariosRef = useRef<Scenario[]>([]);
  const cachedScenariosRef = useRef<CachedScenarioMeta[]>([]);

  const [state, setState] = useState<ModelState>(() => ({
    framework: '',
    dataset: '',
    baselineModel: null,
    baselines: {},
    scenarios: loadFromStorage()?.scenarios || [],
    selectedScenarioIds: loadFromStorage()?.selectedIds || [],
    isLoading: false,
    error: null,
    solvingCount: 0,
    storageWarning: false,
    cachedScenariosList: [],
    frameworkInfo: {},
  }));

  // Keep refs in sync with current state to avoid stale closures in callbacks.
  useEffect(() => {
    frameworkRef.current = state.framework;
    datasetRef.current = state.dataset;
    scenariosRef.current = state.scenarios;
    cachedScenariosRef.current = state.cachedScenariosList;
  }, [state.framework, state.dataset, state.scenarios, state.cachedScenariosList]);

  // Save scenarios to localStorage whenever they change
  useEffect(() => {
    if (state.framework && state.dataset) {
      const saved = saveToStorage(state.framework, state.dataset, state.scenarios, state.selectedScenarioIds);
      setState(previous => previous.storageWarning === !saved ? previous : { ...previous, storageWarning: !saved });
    }
  }, [state.scenarios, state.selectedScenarioIds, state.framework, state.dataset]);

  const refreshCachedScenariosList = useCallback(async () => {
    const lists = await Promise.all(MODEL_TARGETS.map(async target => {
      const result = await apiService.listCachedScenarios(target.framework, target.dataset);
      return result.scenarios.map(s => ({ ...s, framework: target.framework, dataset: target.dataset }));
    }));
    setState(prev => ({ ...prev, cachedScenariosList: lists.flat() }));
  }, []);

  useEffect(() => {
    void refreshCachedScenariosList().catch(() => {});
  }, [refreshCachedScenariosList]);

  // Update solving count
  useEffect(() => {
    const solvingCount = state.scenarios.filter(s => s.status === 'solving').length;
    if (solvingCount !== state.solvingCount) {
      setState(prev => ({ ...prev, solvingCount }));
    }
  }, [state.scenarios, state.solvingCount]);

  // ── Stable callbacks (useCallback with [] deps — all state access via refs or
  //    functional setState so closures never go stale) ──────────────────────────

  const setFramework = useCallback((framework: string) =>
    setState(prev => ({ ...prev, framework })), []);

  const setDataset = useCallback((dataset: string) =>
    setState(prev => ({ ...prev, dataset })), []);

  const clearError = useCallback(() =>
    setState(prev => ({ ...prev, error: null })), []);

  const baselineRequests = useRef(new Map<string, Promise<BaseModel>>());
  const ensureBaseline = useCallback((framework: string, dataset: string, refresh = false) => {
    const key = `${framework}/${dataset}`;
    if (refresh) baselineRequests.current.delete(key);
    let request = baselineRequests.current.get(key);
    if (!request) {
      request = apiService.loadBaselineModel(framework, dataset).then(model => {
        setState(prev => ({ ...prev, baselines: { ...prev.baselines, [key]: model },
          ...(prev.framework === framework && prev.dataset === dataset ? { baselineModel: model } : {}) }));
        return model;
      }).catch(error => { baselineRequests.current.delete(key); throw error; });
      baselineRequests.current.set(key, request);
    }
    return request;
  }, []);

  const loadGeneration = useRef(0);
  const loadModel = useCallback(async (framework: string, dataset: string) => {
    const generation = ++loadGeneration.current;
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const [model, frameworkInfo] = await Promise.all([
        ensureBaseline(framework, dataset), apiService.getFrameworkInfo(),
      ]);
      if (generation !== loadGeneration.current) return;
      frameworkRef.current = framework;
      datasetRef.current = dataset;
      setState(prev => ({ ...prev, framework, dataset, baselineModel: model,
        isLoading: false, frameworkInfo }));
    } catch (err) {
      if (generation === loadGeneration.current) setState(prev => ({ ...prev,
        isLoading: false, error: 'Failed to load model. Please retry.' }));
    }
  }, [ensureBaseline]);

  useEffect(() => {
    const target = MODEL_TARGETS[0];
    void loadModel(target.framework, target.dataset);
  }, [loadModel]);

  /**
   * Process the solve queue sequentially.
   * Uses refs only — safe with empty deps.
   */
  const processQueue = useCallback(async () => {
    if (isProcessingQueueRef.current || solveQueueRef.current.length === 0) return;

    isProcessingQueueRef.current = true;

    while (solveQueueRef.current.length > 0) {
      const item = solveQueueRef.current.shift()!;

      try {
        const results = await apiService.solveCounterfactual(
          item.framework,
          item.dataset,
          item.tariffConfig,
          item.kappaConfig,
        );

        const solvedScenario: Scenario = {
          id: item.id, framework: item.framework, dataset: item.dataset,
          name: item.name,
          tariffConfig: item.tariffConfig,
          kappaConfig: item.kappaConfig,
          result: results,
          status: 'solved',
          createdAt: new Date(),
        };

        setState(prev => ({
          ...prev,
          scenarios: prev.scenarios.map(s => s.id === item.id ? solvedScenario : s),
          selectedScenarioIds: item.isUpdate
            ? prev.selectedScenarioIds
            : [...prev.selectedScenarioIds, item.id],
        }));
      } catch (err: any) {
        console.error(err);
        const detail = err?.response?.data?.detail ?? 'Failed to solve scenario';
        setState(prev => ({
          ...prev,
          scenarios: prev.scenarios.map(s =>
            s.id === item.id
              ? { ...s, status: 'error' as const, error: detail }
              : s
          ),
        }));
      }
    }

    isProcessingQueueRef.current = false;
  }, []);

  /** Import an already solved chat run without changing the manual solve path. */
  const importChatScenario = useCallback((framework: string, dataset: string, scenario: Scenario) => {
    setState(previous => {
      if (previous.scenarios.some(existing => existing.id === scenario.id)) return previous;
      return { ...previous, scenarios: [...previous.scenarios, { ...scenario, framework, dataset }],
        selectedScenarioIds: [...previous.selectedScenarioIds, scenario.id] };
    });
  }, []);

  /**
   * Add a new scenario and solve it. Shocks compose: tariffConfig is always
   * required (pass EMPTY_TARIFF_CONFIG when the tariff section is disabled)
   * and kappaConfig is optional (Furusawa2023-only) — when both carry a
   * real shock they solve together in one request.
   */
  const addScenario = useCallback(async (
    target: ModelTarget,
    name: string,
    tariffConfig: TariffConfiguration,
    kappaConfig?: KappaConfig
  ): Promise<Scenario> => {
    scenarioCounterRef.current += 1;
    const id = `scenario-${scenarioCounterRef.current}-${Date.now()}`;
    const newScenario: Scenario = {
      id,
      framework: target.framework, dataset: target.dataset,
      name,
      tariffConfig,
      kappaConfig,
      result: null,
      status: 'solving',
      createdAt: new Date(),
    };

    // Add scenario with 'solving' status
    setState(prev => ({
      ...prev,
      scenarios: [...prev.scenarios, newScenario],
    }));

    // Add to queue
    solveQueueRef.current.push({
      id,
      framework: target.framework, dataset: target.dataset,
      name,
      tariffConfig,
      kappaConfig,
      isUpdate: false,
    });

    processQueue();

    return newScenario;
  }, [processQueue]);

  /**
   * Update an existing scenario and re-solve it.
   */
  const updateScenario = useCallback(async (
    id: string,
    name: string,
    tariffConfig: TariffConfiguration,
    kappaConfig?: KappaConfig
  ): Promise<Scenario> => {
    const target = scenariosRef.current.find(s => s.id === id);
    if (!target) throw new Error('Scenario not found');
    const updatedScenario: Scenario = {
      id,
      framework: target.framework, dataset: target.dataset,
      name,
      tariffConfig,
      kappaConfig,
      result: null,
      status: 'solving',
      createdAt: new Date(),
    };

    setState(prev => ({
      ...prev,
      scenarios: prev.scenarios.map(s => s.id === id ? updatedScenario : s),
    }));

    // Add to queue
    solveQueueRef.current.push({
      id,
      framework: target.framework, dataset: target.dataset,
      name,
      tariffConfig,
      kappaConfig,
      isUpdate: true,
    });

    processQueue();

    return updatedScenario;
  }, [processQueue]);

  /**
   * Save scenario metadata (name, config) without re-solving.
   * Preserves existing result, baselineComp, and status.
   */
  const saveScenarioMetadata = useCallback((
    id: string,
    name: string,
    tariffConfig: TariffConfiguration,
    kappaConfig?: KappaConfig
  ): void => {
    setState(prev => ({
      ...prev,
      scenarios: prev.scenarios.map(s =>
        s.id === id
          ? { ...s, name, tariffConfig, kappaConfig }
          : s
      ),
    }));
  }, []);

  /**
   * Delete a scenario.
   */
  const deleteScenario = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      scenarios: prev.scenarios.filter(s => s.id !== id),
      selectedScenarioIds: prev.selectedScenarioIds.filter(sid => sid !== id),
    }));
  }, []);

  /**
   * Update which scenarios are selected for visualization/export.
   */
  const updateScenarioSelection = useCallback((ids: string[]) => {
    setState(prev => ({ ...prev, selectedScenarioIds: ids }));
  }, []);

  /**
   * Get the currently selected scenarios.
   * Deps: state slices that determine the result — new reference only when
   * scenarios or selection actually change.
   */
  const getSelectedScenarios = useCallback((): Scenario[] => {
    return state.scenarios.filter(s => state.selectedScenarioIds.includes(s.id));
  }, [state.scenarios, state.selectedScenarioIds]);

  /**
   * Clear all scenarios.
   */
  const clearAllScenarios = useCallback(() => {
    scenarioCounterRef.current = 0;
    setState(prev => ({
      ...prev,
      scenarios: [],
      selectedScenarioIds: [],
    }));
  }, []);

  /**
   * Get the next default scenario name.
   */
  const getNextScenarioName = useCallback((): string => {
    const existingNumbers = scenariosRef.current
      .map(s => {
        const match = s.name.match(/^Scenario (\d+)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);

    const nextNumber = existingNumbers.length > 0
      ? Math.max(...existingNumbers) + 1
      : scenariosRef.current.length + 1;

    return `Scenario ${nextNumber}`;
  }, []);

  /**
   * Save a single scenario to cache.
   * Uses refs for framework/dataset/scenarios — stable with empty deps.
   */
  const saveScenarioToCache = useCallback(async (scenarioId: string) => {
    if (!frameworkRef.current || !datasetRef.current) {
      throw new Error('No model loaded');
    }

    const scenario = scenariosRef.current.find(s => s.id === scenarioId);
    if (!scenario) {
      throw new Error('Scenario not found');
    }
    if (scenario.status !== 'solved' || !scenario.result) {
      throw new Error('Scenario is not solved yet');
    }

    await apiService.saveScenarioToCache(scenario.framework, scenario.dataset, scenario);
    // Refresh the cached scenarios list
    await refreshCachedScenariosList();
  }, [refreshCachedScenariosList]);

  /**
   * Delete a single scenario from cache.
   */
  const deleteScenarioFromCache = useCallback(async (scenarioId: string) => {
    const target = cachedScenariosRef.current.find(s => s.id === scenarioId);
    if (!target) throw new Error('Saved scenario not found');
    await apiService.deleteCachedScenario(target.framework, target.dataset, scenarioId);
    await refreshCachedScenariosList();
  }, [refreshCachedScenariosList]);

  const loadMultipleCachedScenarios = useCallback(async (scenarioIds: string[]) => {
    const loadedScenarios: Scenario[] = [];
    const existingIds = new Set(scenariosRef.current.map(s => s.id));
    for (const id of scenarioIds) {
      if (existingIds.has(id)) continue;
      const target = cachedScenariosRef.current.find(s => s.id === id);
      if (!target) throw new Error('Saved scenario not found');
      const { scenario: s } = await apiService.getCachedScenario(target.framework, target.dataset, id);
      loadedScenarios.push({
        id: s.id, framework: target.framework, dataset: target.dataset,
        name: s.name, tariffConfig: s.tariff_config, kappaConfig: s.kappa_config,
        result: s.result, status: 'solved', createdAt: new Date(s.created_at),
      });
    }
    setState(prev => ({ ...prev,
      scenarios: [...prev.scenarios, ...loadedScenarios],
      selectedScenarioIds: [...prev.selectedScenarioIds, ...loadedScenarios.map(s => s.id)],
    }));
  }, []);

  const clearAllCache = useCallback(async () => {
    await Promise.all(MODEL_TARGETS.map(t => apiService.clearAllCache(t.framework, t.dataset)));
    await refreshCachedScenariosList();
  }, [refreshCachedScenariosList]);

  // ── Memoized provider value ───────────────────────────────────────────────────
  // Stable callback refs (useCallback deps above) prevent the value object from
  // being recreated unless something consumers actually care about changes.
  const contextValue = useMemo(() => ({
    ...state,
    setFramework,
    setDataset,
    loadModel,
    ensureBaseline,
    clearError,
    addScenario,
    importChatScenario,
    updateScenario,
    saveScenarioMetadata,
    deleteScenario,
    updateScenarioSelection,
    getSelectedScenarios,
    clearAllScenarios,
    getNextScenarioName,
    saveScenarioToCache,
    deleteScenarioFromCache,
    loadMultipleCachedScenarios,
    clearAllCache,
    refreshCachedScenariosList,
  }), [
    state,
    setFramework,
    setDataset,
    loadModel,
    ensureBaseline,
    clearError,
    addScenario,
    importChatScenario,
    updateScenario,
    saveScenarioMetadata,
    deleteScenario,
    updateScenarioSelection,
    getSelectedScenarios,
    clearAllScenarios,
    getNextScenarioName,
    saveScenarioToCache,
    deleteScenarioFromCache,
    loadMultipleCachedScenarios,
    clearAllCache,
    refreshCachedScenariosList,
  ]);

  return (
    <ModelContext.Provider value={contextValue}>
      {children}
    </ModelContext.Provider>
  );
};

export const useModel = () => {
  const context = useContext(ModelContext);
  if (context === undefined) {
    throw new Error('useModel must be used within a ModelProvider');
  }
  return context;
};
